import React, { useEffect } from 'react';
import { Alert, Grid, LinearProgress } from '@mui/material';
import DocNotFound from '../tool/DocNotFound';
import ErrorBoundaryInApp from '../../ErrorBoundaryInApp';
import ErrorMsg from '../common/ErrorMsg';
import ReviewDoc from './doc/ReviewDoc';
import ReviewForm from './form/ReviewForm';
import ReviewNav from './ReviewNav';
import Spreadsheet from '../spreadsheet/Spreadsheet';
import { IResultsType } from '../../types/spreadsheet-types';
import errorResponses from '../../errorResponses';
import { AppContext } from '../../App';
import { IDocument, IExtraction, IExtractionBatch } from '../../types/my-types';
import { DocumentDataStatusFilters } from '../../types/packager-types';
import {
  IValidationReactQueries,
} from '../../types/validation-types';
import { featureFlags } from '../../utils/featureFlags';
import useGlynt from '../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import useGetAllDocuments from '../hooks/useGetAllDocuments';
import useGetAllExtractions from '../hooks/useGetAllExtractions';
import { MarkStateContext, MarkDispatchContext } from '../../context/MarkContext';
import UpdateAllExtractionsDialog from '../spreadsheet/UpdateAllExtractionsDialog';
import BulkCorrectionSummaryTableDialog from '../spreadsheet/BulkCorrectionSummaryTableDialog';
import {
  showUpdateAllExtractionsModalAtom,
  showAllValidationErrorsAtom,
  forceValidationAtom,
  validationLoadingAtom,
  validatingExtractionIdsAtom,
  validationNotReadyExtractionIdsAtom,
} from '../../atoms/spreadsheet-atoms';
import ViewAllValidationErrors from '../review/ViewAllValidationErrors';
import ValidationDismissalDialog from '../review/ValidationDismissalDialog';
import { useAtom, useSetAtom } from 'jotai';
import { useDataValidation, validationErrorToastMessage } from '../hooks/useDataValidation';
import {
  deriveSettledExtractionIds,
  useValidationFetchState,
} from '../hooks/useValidationFetchState';
import {
  buildValidationSummaryByExtractionId,
  hasUndismissedValidationErrors,
} from '../../utils/validationUtils';
import { useFullscreenBodyScroll } from '../hooks/useFullscreenBodyScroll';
import {
  ToolContainer,
  NavSection,
  ContentSection,
  LeftPanel,
  RightPanel,
} from '../tool/layout/ToolLayout.styles';

interface ReviewProps {
  ebId: string;
  dpid: string;
  extractionBatch: IExtractionBatch;
  extractionIndex: number;
  extractionIds: string[];
}

const Review: React.FC<ReviewProps> = ({
  ebId,
  dpid,
  extractionBatch,
  extractionIds,
  extractionIndex,
}) => {
  const [showUpdateAllExtractionsModal] = useAtom(showUpdateAllExtractionsModalAtom);
  const [showAllValidationErrors] = useAtom(showAllValidationErrorsAtom);
  const [forceValidation, setForceValidation] = useAtom(forceValidationAtom);
  const [, setValidationLoading] = useAtom(validationLoadingAtom);
  const setValidatingExtractionIds = useSetAtom(validatingExtractionIdsAtom);
  const setValidationNotReadyExtractionIds = useSetAtom(validationNotReadyExtractionIdsAtom);
  // Lifted state: results type for spreadsheet (corrected | normalized)
  const [resultsType, setResultsType] = React.useState<IResultsType>('corrected');
  // Fullscreen toggle state
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [verifiedBannerDismissed, setVerifiedBannerDismissed] = React.useState(false);

  useFullscreenBodyScroll(isFullscreen);

  // TODO: refactor to ReviewContext
  const { showReviewSpreadsheet, setExtractions, setIds, setToast, setFeedback } =
    React.useContext(AppContext);

  const {
    isErrorIgnored,
    prefetchStatus,
    validationsEnabled,
    visibleExtractionIds,
    validationSummaryByExtractionId,
  } = React.useContext(MarkStateContext);

  const dispatch = React.useContext(MarkDispatchContext);

  const glynt = useGlynt();
  const client = useQueryClient();

  const generatePage = useMutation({
    mutationFn: async (docId: string) =>
      (await glynt).document.createPages(docId)
  });

  const validationQueries = useDataValidation(
    validationsEnabled,
    visibleExtractionIds,
    false, // includeTransformed
    forceValidation,
    true, // includeExtracted
    forceValidation // includeSchematized only during explicit revalidation
  );

  const queryErrorMessage = validationErrorToastMessage(validationQueries);
  const isLoading: boolean = validationQueries.some(
    (result: IValidationReactQueries) => result.isLoading
  );
  const isFetching: boolean = validationQueries.some(
    (result: IValidationReactQueries) => result.isFetching
  );
  // Each extraction's result is published as soon as its own query has settled: an
  // extraction that finished validating must not show its previous result while a sibling
  // is still polling, and one exhausted extraction must not hold back the others.
  const settledExtractionIds = deriveSettledExtractionIds(validationQueries, visibleExtractionIds);
  const settledKey = settledExtractionIds.join('|');

  // Per-extraction fetch state, published for the review surfaces (field labels, spreadsheet
  // cells, error list). Derived from the query fetch state only: the validate endpoint
  // answers 202 for as long as a deferred correction holds the extraction, then 200.
  const { validatingExtractionIds, notReadyExtractionIds } = useValidationFetchState(
    validationQueries,
    visibleExtractionIds
  );
  useEffect(() => {
    setValidatingExtractionIds(validatingExtractionIds);
  }, [validatingExtractionIds, setValidatingExtractionIds]);
  useEffect(() => {
    setValidationNotReadyExtractionIds(notReadyExtractionIds);
  }, [notReadyExtractionIds, setValidationNotReadyExtractionIds]);
  useEffect(
    () => () => {
      setValidatingExtractionIds([]);
      setValidationNotReadyExtractionIds([]);
    },
    [setValidatingExtractionIds, setValidationNotReadyExtractionIds]
  );
  const hasNotReadyExtractions = notReadyExtractionIds.length > 0;

  // Update global validation loading state for the icon to use
  useEffect(() => {
    setValidationLoading(isLoading || isFetching);
  }, [isLoading, isFetching, setValidationLoading]);

  // Reset forceValidation after validation completes and refresh extraction batch
  useEffect(() => {
    if (forceValidation && !isLoading && !isFetching) {
      setForceValidation(false);
      client.invalidateQueries({
        queryKey: ['extractionBatch', ebId]
      });
    }
  }, [forceValidation, isLoading, isFetching, setForceValidation, client, ebId]);

  // Corrections and dismissals only invalidate the per-extraction validation queries; the
  // batch-level has_undismissed_validation_errors flag (which the validation badge prefers)
  // goes stale. Refetch the batch whenever the locally derived error state flips (errors
  // cleared or introduced) — those transitions are exactly when the batch flag may change.
  // Comparing against the batch flag itself would misfire when filters hide the batch's
  // remaining errors (a legitimate, persistent mismatch) and would read a stale prop.
  const prevLocalHasErrors = React.useRef<boolean | null>(null);
  useEffect(() => {
    if (validationSummaryByExtractionId.length === 0) {
      prevLocalHasErrors.current = null;
      return;
    }
    const localHasErrors = hasUndismissedValidationErrors(validationSummaryByExtractionId);
    const prev = prevLocalHasErrors.current;
    prevLocalHasErrors.current = localHasErrors;
    if (prev !== null && prev !== localHasErrors) {
      client.invalidateQueries({
        queryKey: ['extractionBatch', ebId]
      });
    }
  }, [validationSummaryByExtractionId, client, ebId]);

  useEffect(() => {
    if (queryErrorMessage) {
      setToast({
        open: true,
        message: queryErrorMessage,
        variant: 'error',
      });
    }
  }, [queryErrorMessage, setToast]);

  useEffect(() => {
    if (hasNotReadyExtractions) {
      setToast({
        open: true,
        message: errorResponses.validationNotReady,
        variant: 'warning',
      });
    }
  }, [hasNotReadyExtractions, setToast]);

  // Initial page load fetches extracted only. Explicit revalidation fetches extracted+schematized together.
  useEffect(() => {
    if (settledExtractionIds.length > 0) {
      const summary = buildValidationSummaryByExtractionId(settledExtractionIds, validationQueries);
      dispatch({
        type: 'SET_VALIDATION_SUMMARY_BY_EXTRACTION_ID',
        payload: summary,
        datasetTypesToClear: ['extracted', 'schematized'],
      });
    }
  }, [
    settledKey,
    forceValidation,
    JSON.stringify(validationQueries),
    JSON.stringify(visibleExtractionIds),
    isErrorIgnored,
  ]);

  const prefetchAllPages = async (allExtractions: IExtraction[]) => {
    if (!allExtractions?.length || allExtractions.length <= 0) {
      return;
    }

    setFeedback({
      open: true,
      message: `Prefetching Extraction Data...`,
    });

    dispatch({ type: 'SET_PREFETCH_STATUS', prefetchStatus: 'loading' });

    try {
      const prefetchPromises = allExtractions?.map(async (extraction) => {
        const tokenizedId = getIdFromUrl(extraction.tokenized);
        const docId = getIdFromUrl(extraction.document);

        // Prefetch Tokenized
        await client.prefetchQuery({
          queryKey: ['tokenized', tokenizedId],
          queryFn: async () => (await glynt).tokenizeds.retrieve(tokenizedId),
          staleTime: 1000 * 60 * 20,
          gcTime: 1000 * 60 * 20
        });

        generatePage.mutate(docId);

        // Get document data so we can prefetch and preload each page
        return client.prefetchQuery({
          queryKey: ['document', docId],

          queryFn: async () => {
            const response = await (await glynt).document.retrieveAdvance(docId);
            const pages = response.pages;

            // Await all page prefetching
            await Promise.all(
              pages.map(async (page) => {
                // remove `https://api-sandbox.glynt.ai/v6` from the url
                const pageKey = page.replace('https://api-sandbox.glynt.ai/v6/', '');

                // Prefetch the page data and preload the image
                await client.prefetchQuery({
                  queryKey: ['page', pageKey],

                  queryFn: async () => {
                    const pageData = await (await glynt).document.getPage(page);
                    if (pageData.file_temp_url) {
                      // Preload the image
                      const imageElement = new Image();
                      imageElement.src = pageData.file_temp_url;
                    }
                    return pageData;
                  },

                  staleTime: 1000 * 60 * 20,
                  gcTime: 1000 * 60 * 20
                });
              })
            );
            return response;
          },

          staleTime: 1000 * 60 * 20,
          gcTime: 1000 * 60 * 20
        });
      });

      // Wait for all prefetch promises to complete
      await Promise.all(prefetchPromises);

      dispatch({ type: 'SET_PREFETCH_STATUS', prefetchStatus: 'done' });
      setFeedback({
        open: true,
        message: 'Prefetching Complete',
      });
    } catch (error) {
      console.error('Error prefetching data:', error);
      dispatch({ type: 'SET_PREFETCH_STATUS', prefetchStatus: 'error' });
      setFeedback({
        open: false,
        message: '',
      });
      setToast({
        open: true,
        message: 'There was an error prefetching data.',
        variant: 'error',
      });
    }
  };

  React.useEffect(() => {
    setIds(extractionIds);
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    // Clear validation state when switching extraction batches to prevent stale data
    dispatch({ type: 'CLEAR_VALIDATION_SUMMARY' });
    dispatch({ type: 'SET_VISIBLE_EXTRACTIONS', payload: [] });
    dispatch({ type: 'SET_PREFETCH_STATUS', prefetchStatus: null });
  }, [ebId]);

  const {
    data: allExtractions,
    isLoading: isLoadingExtractions,
    isFetching: isFetchingExtractions,
    currentPage: currentPageExtractions,
    totalPages: totalPagesExtractions,
    isError: isErrorExtractions,
    totalExtractionsCount,
  } = useGetAllExtractions(ebId, dpid);

  React.useEffect(() => {
    if (
      allExtractions?.length > 0 &&
      !prefetchStatus &&
      showReviewSpreadsheet &&
      !isFetchingExtractions &&
      !isLoadingExtractions &&
      featureFlags.allowPrefetchingInReview
    ) {
      prefetchAllPages(allExtractions);
    }

    if (allExtractions?.length > 0) {
      setExtractions(allExtractions);
    }
  }, [
    allExtractions?.length,
    isFetchingExtractions,
    isLoadingExtractions,
    showReviewSpreadsheet,
    featureFlags.allowPrefetchingInReview,
  ]);

  const {
    data: documents,
    isLoading: isLoadingDocuments,
    isFetching: isFetchingDocuments,
    isError: isErrorDocuments,
  } = useGetAllDocuments({
    group: 'extract',
    groupId: ebId,
    dpid,
  });

  const isStillFetchingExtractions =
    (currentPageExtractions !== 0 &&
      totalPagesExtractions !== 0 &&
      totalPagesExtractions !== currentPageExtractions) ||
    isFetchingExtractions ||
    isLoadingExtractions;

  const currentExtractionEarly = !isStillFetchingExtractions
    ? allExtractions?.[extractionIndex]
    : undefined;

  // Reset banner dismissal when switching extractions so it reappears on each verified doc.
  // Declared before any early returns to keep hook order stable.
  React.useEffect(() => {
    setVerifiedBannerDismissed(false);
  }, [currentExtractionEarly?.id]);

  if (isLoadingExtractions || isLoadingDocuments || isStillFetchingExtractions) {
    return <LinearProgress />;
  }

  if (isErrorDocuments) {
    return <ErrorMsg error={errorResponses.failedToLoadDocuments} />;
  }

  if (isErrorExtractions) {
    return <ErrorMsg error={errorResponses.failedToLoadExtractionBatches} />;
  }

  const docs: IDocument[] = documents || [];
  const extractions: IExtraction[] = allExtractions || [];
  const currentExtraction = !isStillFetchingExtractions ? extractions[extractionIndex] : undefined;
  const docId = getIdFromUrl(currentExtraction?.document || '');
  const isVerified =
    currentExtraction?.document_data_status === DocumentDataStatusFilters.VERIFIED;

  if (!currentExtraction?.id) {
    return <LinearProgress />;
  }

  if (extractionIndex < 0) {
    return <DocNotFound />;
  }

  return (
    <ToolContainer isFullscreen={isFullscreen}>
      {isVerified && !verifiedBannerDismissed && (
        <Alert
          severity="warning"
          variant="filled"
          data-cy="verified-document-banner"
          onClose={() => setVerifiedBannerDismissed(true)}
          sx={{
            borderRadius: 0,
            my: 1,
            fontWeight: 600,
            letterSpacing: '0.5px',
            '& .MuiAlert-message': { textAlign: 'center', flex: 1 },
          }}
        >
          Verified Document - Do Not Modify
        </Alert>
      )}
      <NavSection
        container
        columnSpacing={1}
        rowSpacing={0}
        className="flex-no-shrink"
      >
        <Grid item xs={12}>
          <ReviewNav
            steps={totalExtractionsCount}
            documents={docs}
            docId={docId}
            extractions={extractions}
            extractionId={currentExtraction?.id}
            extractionIndex={extractionIndex}
            batchLabel={extractionBatch.label}
            resultsType={resultsType}
            setResultsType={setResultsType}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
          />
        </Grid>
        {featureFlags.agGrid && showReviewSpreadsheet && (
          <ErrorBoundaryInApp>
            <Grid item xs={12}>
              <Spreadsheet
                extractionBatch={extractionBatch}
                resultsType={resultsType}
                isVerified={isVerified}
              />
            </Grid>
          </ErrorBoundaryInApp>
        )}
      </NavSection>
      <ContentSection
        container
        columnSpacing={1}
        rowSpacing={0}
        className="flex-section flex-flow-row"
        isFullscreen={isFullscreen}
      >
        <LeftPanel item xs={4} lg={3} xl={2} className="flex-col-scroll">
          <ReviewForm extractionId={currentExtraction.id} ebId={ebId} readOnly={isVerified} />
        </LeftPanel>
        <RightPanel
          item
          xs={8}
          lg={9}
          xl={10}
          className="flex-col-scroll"
          id="doc-page-wrapper"
          isFullscreen={isFullscreen}
        >
          <ReviewDoc extraction={currentExtraction} />
        </RightPanel>
      </ContentSection>
      {showUpdateAllExtractionsModal ? <UpdateAllExtractionsDialog /> : null}
      {showAllValidationErrors ? <ViewAllValidationErrors /> : null}
      <ValidationDismissalDialog />
      <BulkCorrectionSummaryTableDialog />
    </ToolContainer>
  );
};

export default Review;
